import { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Upload, RefreshCw } from 'lucide-react';
import Panel from './Panel.jsx';
import DocumentsPanel from './DocumentsPanel.jsx';
import CarrierDocAttach, { ledgerRowName } from './CarrierDocAttach.jsx';
import PolicyDocsDropSheet from '@/components/sheets/PolicyDocsDropSheet.jsx';
import RegenerateBindingAppDialog from '@/components/sheets/RegenerateBindingAppDialog.jsx';
import SendStandardSetButton from '@/components/paperwork/SendStandardSetButton.jsx';
import { WaiverStanding } from '@/components/paperwork/PaymentHoldBlock.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { listPolicyDocs } from '@/services/submission.service.js';
import { refileDocumentType } from '@/services/quoteIngest.service.js';
import { getDealPaperwork, undoDocumentReplacement, confirmPaperworkDocument } from '@/services/paperwork.service.js';
import { placeDealDocument, renameDealDocument, getDocumentMoveChoices, moveDealDocument, fileDealDocuments, DOCUMENTS_CHANGED_EVENT, LEDGER_CHANGED_EVENT, announceDocumentsChanged } from '@/services/deals.service.js';
import { uploadInterestCert, updateInterest } from '@/services/interests.service.js';
import { RETYPE_KINDS, classLabel } from '@/lib/dropKinds.js';
import RetypeControl from './RetypeControl.jsx';
import { DOCUMENT_CLASS_WORDS } from '@/components/v3/words.js';
import useDeal from '@/hooks/useDeal.js';
import { useRegenerateBinder } from '@/hooks/useRegenerateBinder.js';
import { openDocument, openStoredDocument } from '@/lib/openDocument.js';

// THE PAPERWORK TAB (paperwork-1 screen 1, D6; L2-1): what this policy's paperwork needs, and who has it. It replaces the
// Docs tab and its client-side checklist (docChecklist.js, gone): every item, its state and its button words are the
// server's (GET /api/tracon/deals/:dealId/paperwork, utils/paperwork-checklist.js, one derivation); this file derives nothing.
// Kept from the Docs tab: Review paperwork, Regenerate binding application, the invoice row with Ledger and Statement, the
// selected quote row, Upload (the L1b drop sheet), the filed list (now the documents home's, by derived name, with Open and
// "Wrong type? Change it"), the inbox filings still in WorkDrive with their re-file control, and the account-wide WorkDrive
// tree. Added: each missing item's button, "Undo the replacement" on a replaced document, "Confirm it is this policy's" on an
// unconfirmed one, and the standard set's button (L1a's stub, filled by L3). Rework accounting: docs/paperwork-1/REWORK-L2.md.

const btn = 'rounded-md border border-border-strong px-2.5 py-2 text-[11px] text-text hover:bg-surface-strong disabled:opacity-50 sm:py-1';
const btnPrimary = 'rounded-md border border-accent bg-accent px-2.5 py-2 text-[11px] text-white hover:brightness-110 disabled:opacity-50 sm:py-1';
// The money intake's classes: never confirmed by a person (the intake resolves them), never on the checklist.
const MONEY_CLASSES = new Set(['receipt', 'statement', 'invoice', 'credit_memo', 'bank_export']);
const errorWords = (e) => e?.body?.error || e?.message || 'failed';

function StatusDot({ state, optional, children }) {
  const tone = state === 'filed' ? 'done' : state === 'missing' && !optional ? 'pend' : state === 'unknown' ? 'info' : 'quiet';
  const cls = tone === 'done' ? 'text-success' : tone === 'quiet' ? 'text-text-dim' : tone === 'info' ? 'text-info' : 'text-warning';
  const dot = tone === 'done' ? 'bg-success' : tone === 'quiet' ? 'bg-border-strong' : tone === 'info' ? 'bg-info' : 'bg-warning';
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${cls}`} data-item-state={state}>
      <span className={`h-[7px] w-[7px] rounded-full ${dot}`} />{children}
    </span>
  );
}

// The certificate for one interest, picked and filed through the deal's document door (kind certificate, naming it).
function CertificatePick({ dealId, interestId, label, onFiled }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadInterestCert(interestId, dealId, file, { replaces: 'none' });
      toast.success(`${file.name} filed as ${r.document?.name || 'the certificate'}`);
      onFiled?.();
    } catch (e) { toast.error(`${file.name}: ${errorWords(e)}`); } finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  return (
    <>
      <input ref={input} type="file" accept="application/pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      <button type="button" className={btnPrimary} disabled={busy} onClick={() => input.current?.click()} data-action="drop_certificate">{busy ? 'Filing…' : label}</button>
    </>
  );
}

// A loss-payee endorsement naming one holder (Q19, David 2026-10-31), filed through the deal's document door as kind
// endorsement naming it (unattached to a ledger row; "Wrong type? Change it" and the attach link it later).
function HolderEndorsementPick({ dealId, interestId, label, onFiled }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const body = await fileDealDocuments(dealId, [{ file, kind: 'endorsement', interest_id: interestId }]);
      const r = body.results[0] || {};
      if (!r.ok) throw new Error(r.error || 'Not filed');
      toast.success(`${file.name} filed as ${r.document?.name || 'the loss-payee endorsement'}`);
      onFiled?.();
    } catch (e) { toast.error(`${file.name}: ${errorWords(e)}`); } finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  return (
    <>
      <input ref={input} type="file" accept="application/pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      <button type="button" className={btnPrimary} disabled={busy} onClick={() => input.current?.click()} data-action="drop_holder_endorsement">{busy ? 'Filing…' : label}</button>
    </>
  );
}

// An interest's email, typed here and saved on the interest (the interests panel's own PATCH door).
function InterestEmail({ interestId, label, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) return <button type="button" className={btn} onClick={() => setOpen(true)} data-action="edit_interest_email">{label}</button>;
  const save = async () => {
    setBusy(true);
    try {
      const r = await updateInterest(interestId, { email: value.trim() });
      toast.success(`Email saved for ${r?.interest?.name || 'the interest'}: ${r?.interest?.email || value.trim()}`);
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(errorWords(e)); } finally { setBusy(false); }
  };
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <input type="email" value={value} onChange={(e) => setValue(e.target.value)} placeholder="name@example.com" aria-label={label}
        className="min-h-[28px] rounded-card border border-border bg-bg px-2 text-[12px] text-text" />
      <button type="button" className={btnPrimary} disabled={busy || !value.trim()} onClick={save}>Save the email</button>
      <button type="button" className={btn} disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
    </span>
  );
}

function ItemAction({ a, item, ctx }) {
  const [kind, id] = String(a.key).split(/:(.+)/);
  if (kind === 'drop_policy') return <button type="button" className={btnPrimary} onClick={() => ctx.openDrop('policy')} data-action={kind}><Upload size={12} strokeWidth={1.7} className="mr-1 inline" />{a.label}</button>;
  if (kind === 'drop_binder') return <button type="button" className={btnPrimary} onClick={() => ctx.openDrop('binder')} data-action={kind}><Upload size={12} strokeWidth={1.7} className="mr-1 inline" />{a.label}</button>;
  // paperwork-1 L2-2 (D9): the window's button opens the drop with each file preselected Signed application.
  if (kind === 'drop_signed_application') return <button type="button" className={btnPrimary} onClick={() => ctx.openDrop('application')} data-action={kind}><Upload size={12} strokeWidth={1.7} className="mr-1 inline" />{a.label}</button>;
  if (kind === 'review_paperwork') return <button type="button" className={btnPrimary} onClick={ctx.reviewPaperwork} data-action={kind}>{a.label}</button>;
  // David 2026-11-01: "one press regenerates, then opens the review": a fresh draft first, then the review (newest pending first,
  // so it shows the draft just rendered); a failed render says why and opens nothing.
  if (kind === 'regenerate_binder') return <button type="button" className={btnPrimary} disabled={ctx.binder.regenerating} onClick={ctx.regenerateBinder} data-action={kind}>{ctx.binder.regenerating ? 'Regenerating the binder...' : a.label}</button>;
  if (kind === 'confirm_document') {
    const d = (item.documents || []).find((x) => String(x.id) === id);
    return <button type="button" className={btnPrimary} disabled={ctx.busy === a.key} onClick={() => ctx.confirm(id, d?.name)} aria-label={`${a.label}: ${d?.name || 'this document'}`} data-action={kind}>{a.label}</button>;
  }
  if (kind === 'attach_carrier_document' && item.transaction) {
    return <CarrierDocAttach dealId={ctx.dealId} txnId={id} rowName={ledgerRowName(item.transaction.type, item.transaction.effective_date)} onAttached={ctx.changed} />;
  }
  if (kind === 'drop_certificate') return <CertificatePick dealId={ctx.dealId} interestId={id} label={a.label} onFiled={ctx.changed} />;
  // Q19: either document satisfies a lienholder or loss payee, so both are offered, each naming the holder.
  if (kind === 'drop_holder_document') {
    const name = item.interest?.name || 'this holder';
    return (
      <span className="flex flex-wrap items-center gap-1.5" data-action={kind}>
        <CertificatePick dealId={ctx.dealId} interestId={id} label={`Drop the certificate for ${name}`} onFiled={ctx.changed} />
        <HolderEndorsementPick dealId={ctx.dealId} interestId={id} label={`Drop the loss-payee endorsement for ${name}`} onFiled={ctx.changed} />
      </span>
    );
  }
  // Q10: the standard set not yet delivered; the button opens the send sheet for this policy.
  if (kind === 'send_standard_set') return ctx.policy ? <SendStandardSetButton dealId={ctx.dealId} policyId={ctx.policy.id} policyLabel={ctx.policy.label} onSent={ctx.changed} /> : null;
  if (kind === 'edit_interest_email') return <InterestEmail interestId={id} label={a.label} onSaved={ctx.changed} />;
  if (kind === 'open_ledger') return typeof ctx.onOpenTrust === 'function' ? <button type="button" className={btn} onClick={ctx.onOpenTrust} data-action={kind}>{a.label}</button> : null;
  if (kind === 'open_statement') return item.open?.permalink ? <button type="button" className={btn} onClick={() => openDocument(item.open.permalink)} data-action={kind}>{a.label}</button> : null;
  // too_large and anything the server adds later: the words, no button that would do nothing.
  return <span className="text-[11px] text-text-muted" data-action={kind}>{a.label}</span>;
}

function ChecklistRow({ item, ctx }) {
  const openers = [];
  if (item.state === 'filed') {
    for (const d of item.documents || []) openers.push(<button key={d.id} type="button" className={btn} onClick={() => openStoredDocument(d.id)} aria-label={`Open ${d.name}`}>Open</button>);
    // The term's signed applications (the wizard's and each upload, David 2026-10-31: an upload is its own record), newest
    // first, each with its own Open naming it; the server's open is the newest, so it is not repeated.
    const files = (item.files || []).filter((f) => f.permalink);
    if (files.length > 1) for (const f of files) openers.push(<button key={`file-${f.id}`} type="button" className={btn} onClick={() => openDocument(f.permalink)} aria-label={`Open ${f.name || 'this application'}`} data-file={f.uploaded ? 'uploaded' : 'tracon'}>{f.open_label}</button>);
    else if (item.open?.permalink && item.key !== 'invoice') openers.push(<button key="open" type="button" className={btn} onClick={() => openDocument(item.open.permalink)} aria-label={`Open the ${item.label}`}>Open</button>);
    if (item.regenerate) {
      openers.push(<button key="regen" type="button" className={btn} onClick={ctx.openRegen} data-action="regenerate_application">
        <RefreshCw size={12} strokeWidth={1.7} className="mr-1 inline" />Regenerate binding application
      </button>);
    }
  }
  const actions = item.state === 'filed' && item.key !== 'invoice' ? [] : item.actions || [];
  const named = item.state !== 'filed' && (item.documents || []).length ? item.documents : [];
  // Stacked, not three columns: the deal page's main column is narrow, and a long button (the attach names its ledger row)
  // or a long label must wrap inside the row rather than run past it (Chrome pass, L2-1).
  const buttons = [...openers, ...actions.map((a) => <ItemAction key={a.key} a={a} item={item} ctx={ctx} />)];
  return (
    <div className="border-t border-border p-3 text-[13px] first:border-t-0" data-paperwork-item={item.key}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex min-w-0 items-start gap-2"><FileText size={14} strokeWidth={1.7} className="mt-0.5 shrink-0 text-text-muted" /><span className="min-w-0">{item.label}</span></span>
        <StatusDot state={item.state} optional={item.optional}>{item.status}</StatusDot>
      </div>
      {(item.state === 'filed' ? item.documents || [] : named).length ? (
        <div className="mt-0.5 break-words pl-6 text-[11px] text-text-muted">{(item.state === 'filed' ? item.documents : named).map((d) => d.name).join(', ')}</div>
      ) : null}
      {(item.files || []).length ? (
        <ul className="mt-0.5 break-words pl-6 text-[11px] text-text-muted" data-item-files>
          {item.files.map((f, i) => <li key={f.id}>{f.name || (f.uploaded ? 'Application, uploaded' : 'Application')}{item.files.length > 1 && i === 0 ? ' (newest, the one that counts)' : ''}</li>)}
        </ul>
      ) : null}
      {buttons.length ? <div className="mt-2 flex flex-wrap items-center gap-2 pl-6 [&_button]:h-auto [&_button]:whitespace-normal [&_button]:text-left">{buttons}</div> : null}
    </div>
  );
}

// "Wrong policy? Move it" (review fix M4): the server's policies (this account's first), a filter, and one button that names
// where it goes. The move clears the confirmation, so the answer says it is to be confirmed on its new policy.
function MovePicker({ doc, ctx, onClose }) {
  const [choices, setChoices] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [pick, setPick] = useState('');
  useEffect(() => {
    getDocumentMoveChoices(doc.id).then((r) => setChoices(r.policies || [])).catch((e) => setError(`The policies could not be read: ${errorWords(e)}`));
  }, [doc.id]);
  const f = filter.trim().toLowerCase();
  const shown = (choices || []).filter((p) => !f || p.label.toLowerCase().includes(f)).slice(0, 50);
  const target = (choices || []).find((p) => String(p.id) === String(pick)) || null;
  return (
    <div className="flex flex-col gap-1.5 pl-5 text-text-muted" data-move-picker={doc.id}>
      {error ? <span className="text-danger" data-move-error>{error}</span> : choices === null ? <span>Reading the policies...</span> : (
        <>
          <label className="flex flex-col gap-1">
            Find the policy it belongs on (account name, policy number or term)
            <input value={filter} onChange={(e) => setFilter(e.target.value)} className="min-h-[28px] rounded-card border border-border bg-bg px-1.5 text-[12px] text-text" data-move-filter />
          </label>
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="min-h-[28px] rounded-card border border-border bg-bg px-1.5 text-[12px] text-text" data-move-choice aria-label={`The policy ${doc.name} belongs on`}>
            <option value="">{shown.length ? `Choose the policy (${shown.length} shown)` : 'No policy matches those words'}</option>
            {shown.map((p) => <option key={p.id} value={p.id}>{p.same_account ? p.label : `${p.label} (another account)`}</option>)}
          </select>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btnPrimary} disabled={!target || ctx.busy === `move:${doc.id}`} onClick={() => ctx.move(doc, target, onClose)} data-action="move_document">
              {target ? `Move it to ${target.label}` : 'Choose a policy first'}
            </button>
            <button type="button" className={btn} onClick={onClose}>Keep it here</button>
          </div>
        </>
      )}
    </div>
  );
}

// "Rename" (N14, David 2026-11-01): the document's name is derived (the insured, the tail or project, the term, the kind, the
// carrier) and a stated name wins everywhere it is named: here, a download, an attachment. Clearing it returns the derived
// name, which is always shown beside a stated one. The answer is the server's row read back.
function RenameHandle({ doc, ctx }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const busy = ctx.busy === `rename:${doc.id}`;
  const typed = value.trim();
  if (!open) {
    return (
      <span className="ml-5 flex flex-wrap items-center gap-2 text-text-muted" data-rename={doc.id}>
        {doc.name_overridden ? <span data-derived-name>Named by hand; its derived name is {doc.derived_name}</span> : null}
        <button type="button" className={btn} onClick={() => { setValue(doc.name || ''); setOpen(true); }} data-action="open_rename" aria-label={`Rename ${doc.name}`}>Rename</button>
        {doc.name_overridden ? <button type="button" className={btn} disabled={busy} onClick={() => ctx.rename(doc, null)} data-action="clear_rename">Use the derived name {doc.derived_name}</button> : null}
      </span>
    );
  }
  return (
    <span className="ml-5 flex flex-wrap items-center gap-2 text-text-muted" data-rename={doc.id}>
      <input value={value} onChange={(e) => setValue(e.target.value)} maxLength={200} aria-label={`The name ${doc.name} goes by`}
        className="min-h-[28px] w-full min-w-0 max-w-full basis-full rounded-card border border-border bg-bg px-2 text-[12px] text-text sm:basis-auto sm:min-w-[20rem] sm:flex-1" data-rename-input />
      <button type="button" className={btnPrimary} disabled={busy || !typed || typed === doc.name} onClick={() => ctx.rename(doc, typed, () => setOpen(false))} data-action="save_rename">
        {typed && typed !== doc.name ? `Rename it ${typed}` : 'Type the new name first'}
      </button>
      <button type="button" className={btn} disabled={busy} onClick={() => setOpen(false)}>Keep {doc.name}</button>
    </span>
  );
}

function MoveHandle({ doc, ctx }) {
  const [open, setOpen] = useState(false);
  if (!open) return <button type="button" className={`${btn} ml-5 self-start`} onClick={() => setOpen(true)} data-action="open_move" aria-label={`Wrong policy? Move ${doc.name}`}>Wrong policy? Move it</button>;
  return <MovePicker doc={doc} ctx={ctx} onClose={() => setOpen(false)} />;
}

// The documents home's rows for this policy: by derived name, Open, the kind with "Wrong type? Change it", and on a replaced
// row "Undo: <old> is no longer replaced by <new>". A document in a replacement chain keeps its kind (placeDocument refuses
// the re-type, D-L1b-B1-10), so it is offered no re-type; the undo is the way out of the chain.
function FiledList({ documents, ctx }) {
  if (!documents?.length) return <p className="mt-3 text-[12px] text-text-muted" data-filed-empty>Nothing is filed on this policy in TRACON yet.</p>;
  const byId = new Map(documents.map((d) => [String(d.id), d]));
  const replacing = new Map(documents.filter((d) => d.superseded_by).map((d) => [String(d.superseded_by), d]));
  return (
    <div className="mt-3" data-filed-to-policy>
      <div className="mb-1.5 text-[10px] uppercase tracking-[0.12em] text-text-dim">Filed to this policy</div>
      <ul className="overflow-hidden rounded-card border border-border">
        {documents.map((d) => {
          const newer = d.superseded_by ? byId.get(String(d.superseded_by)) : null;
          const older = replacing.get(String(d.id));
          // Two documents of one kind on one policy share a derived name; the files they came from tell them apart.
          const same = newer && newer.name === d.name;
          const named = (x) => (same && x.original_filename ? `${x.name} (${x.original_filename})` : x.name);
          const undoLabel = `Undo: ${named(d)} is no longer replaced by ${newer ? named(newer) : 'the later document'}`;
          return (
            <li key={d.id} className="flex flex-col gap-1.5 border-t border-border p-2.5 text-xs first:border-t-0" data-filed-row={d.id}>
              <div className="flex flex-wrap items-center gap-2">
                <FileText size={12} strokeWidth={1.5} className="shrink-0 text-text-dim" />
                <button type="button" onClick={() => openStoredDocument(d.id)} className={`text-left hover:text-accent ${d.superseded ? 'text-text-dim line-through' : 'text-text'}`} aria-label={`Open ${d.name}`}>{d.name}</button>
                {d.original_filename ? <span className="text-text-dim">filed from {d.original_filename}</span> : null}
                <span className="font-mono text-[10px] uppercase tracking-wide text-text-muted">{DOCUMENT_CLASS_WORDS[d.class] || classLabel(d.class)}</span>
                {/* A8: an unconfirmed carrier or other document (an import) is not sendable until a person confirms it; a money
                    document is the intake's and is never confirmed, so it says nothing here (Chrome pass, L2-1). */}
                {!d.confirmed && !MONEY_CLASSES.has(d.class) && !d.superseded ? (
                  <>
                    <span className="text-[10px] text-warning">Not confirmed as this policy's</span>
                    <button type="button" className={btn} disabled={ctx.busy === `confirm_document:${d.id}`} onClick={() => ctx.confirm(d.id, d.name)} aria-label={`Confirm it is this policy's: ${d.name}`} data-action="confirm_document">Confirm it is this policy's</button>
                  </>
                ) : null}
              </div>
              {d.superseded ? (
                <div className="flex flex-wrap items-center gap-2 pl-5 text-text-muted" data-replaced-by={d.superseded_by}>
                  <span>Replaced by {newer ? named(newer) : 'a later document'}.</span>
                  <button type="button" className={btn} disabled={ctx.busy === `undo:${d.id}`} onClick={() => ctx.undo(d, newer)} data-action="undo_replacement">{undoLabel}</button>
                </div>
              ) : older ? (
                <div className="pl-5 text-text-muted" data-replaces={older.id}>It replaces {older.name}{older.original_filename ? ` (${older.original_filename})` : ''}; a document that replaced another keeps its kind.</div>
              ) : (
                // N5 (David 2026-11-01): every filed document may be re-typed to any kind, the signed application included.
                <RetypeControl doc={d} dealId={ctx.dealId} kinds={RETYPE_KINDS} disabled={ctx.busy === `retype:${d.id}`} onRetype={(kind, extra) => ctx.retype(d, kind, extra)} className="pl-5" />
              )}
              <RenameHandle doc={d} ctx={ctx} />
              {!d.superseded && !older && d.can_move ? <MoveHandle doc={d} ctx={ctx} /> : null}
              {ctx.rowError?.id === d.id ? <div className="pl-5 text-danger" data-row-error>{ctx.rowError.text}</div> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// The inbox's filings that stay in WorkDrive this round (quotes, invoices, renewal notices, claims, others; A4's bundle and
// signed application rows): their type is still a control through the inbox re-file door. A row of the documents home is in
// the list above, never here.
const REFILE_TYPES = ['quote', 'binder', 'policy_doc', 'certificate', 'endorsement', 'invoice', 'renewal_notice', 'claim', 'other'];
function WorkDrivePaper({ rows, onRefiled }) {
  const [busyId, setBusyId] = useState(null);
  if (!rows?.length) return null;
  const label = (d) => String(d.confirmed_type || d.document_type || 'document').replace(/_/g, ' ');
  const refile = async (d, next) => {
    if (!next || next === (d.confirmed_type || d.document_type)) return;
    setBusyId(d.id);
    try { await refileDocumentType(d.id, next); toast.success(`${d.filename || label(d)} re-filed as ${next.replace(/_/g, ' ')}`); onRefiled?.(); }
    catch (e) { toast.error(e?.message || 'Could not re-file this document'); } finally { setBusyId(null); }
  };
  return (
    <div className="mt-3" data-workdrive-paper>
      <div className="mb-1.5 text-[10px] uppercase tracking-[0.12em] text-text-dim">Other filed paper, still in WorkDrive</div>
      <ul className="overflow-hidden rounded-card border border-border">
        {rows.map((d) => (
          <li key={d.id} className="flex flex-col gap-1 border-t border-border p-2.5 text-xs first:border-t-0 sm:flex-row sm:items-center sm:gap-3">
            <span className="flex flex-1 items-center gap-1.5 truncate">
              <FileText size={12} strokeWidth={1.5} className="shrink-0 text-text-dim" />
              {d.workdrive_permalink ? <button type="button" onClick={() => openDocument(d.workdrive_permalink)} className="truncate text-left text-text-muted hover:text-accent">{d.filename || label(d)}</button>
                : <span className="truncate text-text-muted">{d.filename || label(d)}</span>}
            </span>
            <select value={d.confirmed_type || d.document_type || 'other'} disabled={busyId === d.id} onChange={(e) => refile(d, e.target.value)} aria-label={`Re-file ${d.filename || label(d)}`}
              className="rounded border border-border bg-surface px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-text-muted hover:border-accent disabled:opacity-50">
              {[...new Set([d.confirmed_type || d.document_type || 'other', ...REFILE_TYPES])].map((t) => <option key={t} value={t}>{String(t).replace(/_/g, ' ')}</option>)}
            </select>
            <span className={`text-[10px] ${d.client_visible ? 'text-success' : 'text-text-dim'}`}>{d.client_visible ? 'Published to client' : 'Broker only'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// accountId is accepted from the caller (DealWorkspace passes it) and unused, as the Docs tab's was.
export default function PaperworkTab({ dealId, documents: workdriveTree, onChange, onReviewPaperwork, onOpenTrust }) {
  const [answer, setAnswer] = useState(null); // the server's { policy, checklist, documents, unread }
  const [readError, setReadError] = useState(null); // { code, text }
  const [inbound, setInbound] = useState([]);
  const [busy, setBusy] = useState(null);
  const [rowError, setRowError] = useState(null);
  const [dropOpen, setDropOpen] = useState(false);
  const [dropKind, setDropKind] = useState(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const { data: dealRes, refresh: refreshDeal } = useDeal(dealId || null);
  const dealItem = dealRes?.deal || null;
  // Regenerate guard: a session on the deal means the client signature was captured at bind time (the dialog says so).
  const hasSession = !!(dealItem?.session_token || dealItem?.session_id);
  const accountName = dealItem?.account_name || dealItem?.insured || dealItem?.deal_name || null;

  const load = useCallback(() => {
    if (!dealId) return;
    getDealPaperwork(dealId).then((r) => { setAnswer(r); setReadError(null); })
      .catch((e) => { setAnswer(null); setReadError({ code: e?.body?.code || null, text: errorWords(e) }); });
    // A failed read is not "nothing on file" (main 46c07e1, carried from DocsTab): say so, keep what was shown.
    listPolicyDocs(dealId).then((r) => setInbound((r?.documents || []).filter((d) => d.source !== 'documents' && !d.stored_document_id)))
      .catch((err) => toast.error(`Filed policy documents could not be read: ${err?.body?.error || err?.message || 'request failed'}`));
  }, [dealId]);
  useEffect(() => { load(); }, [load]);
  // A drop, a re-type, an attach or a ledger change elsewhere on the page re-reads the checklist.
  useEffect(() => {
    const on = (ev) => { if (String(ev?.detail?.dealId) === String(dealId)) load(); };
    window.addEventListener(DOCUMENTS_CHANGED_EVENT, on);
    window.addEventListener(LEDGER_CHANGED_EVENT, on);
    return () => { window.removeEventListener(DOCUMENTS_CHANGED_EVENT, on); window.removeEventListener(LEDGER_CHANGED_EVENT, on); };
  }, [dealId, load]);

  const changed = useCallback(() => { announceDocumentsChanged(dealId); if (typeof onChange === 'function') onChange(); }, [dealId, onChange]);
  // After a regen: refresh the deal so the signed-application link updates, and reload the checklist.
  const binder = useRegenerateBinder(dealId);
  const handleRegenerated = useCallback(() => {
    if (typeof refreshDeal === 'function') refreshDeal();
    changed();
  }, [refreshDeal, changed]);

  const act = async (key, id, fn, done) => {
    setBusy(key); setRowError(null);
    try { const r = await fn(); toast.success(done(r)); changed(); }
    catch (e) { setRowError({ id, text: errorWords(e) }); toast.error(errorWords(e)); } finally { setBusy(null); }
  };
  const ctx = {
    dealId, busy, rowError, onOpenTrust, changed, policy: answer?.policy ? { id: answer.policy.id, label: answer?.policy?.label || dealItem?.deal_name || 'this policy' } : null,
    openDrop: (kind = null) => { setDropKind(kind); setDropOpen(true); },
    openRegen: () => setRegenOpen(true),
    reviewPaperwork: () => (onReviewPaperwork ? onReviewPaperwork() : toast('Open the deal to review paperwork')),
    binder,
    regenerateBinder: async () => { if (await binder.regenerate()) { changed(); if (onReviewPaperwork) onReviewPaperwork(); else toast('Open the deal to review paperwork'); } },
    confirm: (id, name) => act(`confirm_document:${id}`, id, () => confirmPaperworkDocument(dealId, id), (r) => `${r?.document?.name || name || 'The document'} is confirmed as this policy's`),
    // The toast names both files when the two share a derived name (two copies of one kind on one policy always do).
    undo: (d, newer) => act(`undo:${d.id}`, d.id, () => undoDocumentReplacement(dealId, d.id, d.superseded_by), () => {
      const same = newer && newer.name === d.name;
      const n = (x) => (same && x.original_filename ? `${x.name} (${x.original_filename})` : x.name);
      return `${n(d)} is no longer replaced by ${newer ? n(newer) : 'the later document'}`;
    }),
    // N5: the server's answer is said: the new name, the signing day of an application it filed, an application it took out of
    // the window, and a partial filing as an error on the row (the checklist is read again either way).
    retype: (d, kind, extra = {}) => act(`retype:${d.id}`, d.id, async () => {
      const r = await placeDealDocument(d.id, { kind, ...extra });
      if (r?.partial) { changed(); throw new Error(r.partial); }
      return r;
    }, (r) => {
      const named = `${d.name} is now filed as ${r?.document?.name || classLabel(kind)}`;
      if (kind === 'application' && r?.application) return `${named}, the signed application signed ${r.application.signed_on || extra.signed_on}`;
      return (r?.unfiled || []).length ? `${named}. It no longer counts as the signed application` : named;
    }),
    rename: (d, name, onDone) => act(`rename:${d.id}`, d.id, async () => { const r = await renameDealDocument(d.id, name); onDone?.(); return r; },
      (r) => (name === null ? `${d.name} goes by its derived name again: ${r?.document?.name || 'read it again'}` : `${d.derived_name || d.name} is now named ${r?.document?.name || name}`)),
    move: (d, target, onDone) => act(`move:${d.id}`, d.id, async () => { const r = await moveDealDocument(d.id, target.id); onDone?.(); return r; },
      (r) => `${r?.document?.name || d.name} moved to ${target.label}${r?.confirm_again ? '. Confirm it there: it is not sent until it is confirmed on that policy' : ''}`),
  };

  const cl = answer?.checklist || null;
  const policyLabel = answer?.policy?.label || dealItem?.deal_name || 'this policy';
  return (
    <>
      <Panel title="Paperwork">
        <p className="mb-3 text-[12px] text-text-muted" data-paperwork-purpose>What this policy's paperwork needs, and who has it.</p>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {answer?.policy ? <SendStandardSetButton dealId={dealId} policyId={answer.policy.id} policyLabel={policyLabel} /> : null}
          <button type="button" className={btn} onClick={() => ctx.openDrop(null)} data-action="upload"><Upload size={12} strokeWidth={1.7} className="mr-1 inline" />Upload documents for {policyLabel}</button>
          {cl ? <span className="text-[11px] text-text-muted" data-paperwork-count>{cl.counts.outstanding > 0 ? `${cl.counts.outstanding} still needed` : 'Nothing still needed'}{cl.counts.unknown ? `, ${cl.counts.unknown} could not be checked` : ''}</span> : null}
        </div>
        {readError ? (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]" data-paperwork-error={readError.code || 'failed'}>
            <span className="text-danger">{readError.code === 'NO_POLICY_ROW' ? 'This deal has no policy record in TRACON yet, so its paperwork cannot be checked.' : `The paperwork could not be read: ${readError.text}`}</span>
            {readError.code === 'NO_POLICY_ROW' ? null : <button type="button" className={btn} onClick={load}>Read the paperwork again</button>}
          </div>
        ) : null}
        {answer?.unread?.length ? (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px] text-info" data-paperwork-unread>
            <span>Could not check: {answer.unread.map((u) => u.fact.replace(/_/g, ' ')).join(', ')}.</span>
            <button type="button" className={btn} onClick={load}>Read the paperwork again</button>
          </div>
        ) : null}
        {cl ? (
          <div className="overflow-hidden rounded-card border border-border" data-paperwork-checklist>
            {cl.items.map((i) => <ChecklistRow key={i.key} item={i} ctx={ctx} />)}
          </div>
        ) : null}
        {/* The waiver (round 2, David Q5): shown only while one is live, with its end and the owner's revoke. */}
        {answer?.policy ? <div className="mt-3"><WaiverStanding dealId={dealId} policyLabel={policyLabel} /></div> : null}
        {cl?.not_in_standard_set && (cl.not_in_standard_set.forwarded || []).length ? (
          <p className="mt-2 text-[11px] text-text-muted" data-forwarded>
            No email on file, so the certificate goes on the client's email to forward: {cl.not_in_standard_set.forwarded.map((i) => `${i.name} (${i.type || 'interest'})`).join(', ')}.
          </p>
        ) : null}
        {cl?.not_in_standard_set && (cl.not_in_standard_set.orphaned.length || cl.not_in_standard_set.other_interests.length) ? (
          <div className="mt-2 space-y-1 text-[11px] text-text-muted" data-not-in-standard-set>
            {cl.not_in_standard_set.other_interests.map((i) => (
              <div key={i.interest_id} className="flex flex-wrap items-center gap-2">
                <span>{i.name} ({i.type || 'interest'}): no certificate on file, so nothing goes to them.</span>
                <CertificatePick dealId={dealId} interestId={i.interest_id} label={`Drop the certificate for ${i.name}`} onFiled={changed} />
              </div>
            ))}
            {cl.not_in_standard_set.orphaned.length ? <p>Not in the standard set: {cl.not_in_standard_set.orphaned.map((i) => `${i.name} (aircraft not on this policy)`).join(', ')}.</p> : null}
          </div>
        ) : null}
        {answer ? (answer.documents ? <FiledList documents={answer.documents} ctx={ctx} /> : null) : null}
        <WorkDrivePaper rows={inbound} onRefiled={changed} />
        <PolicyDocsDropSheet dealId={dealId} open={dropOpen} onOpenChange={setDropOpen} onSuccess={changed} initialKind={dropKind} />
        <RegenerateBindingAppDialog dealId={dealId} open={regenOpen} onOpenChange={setRegenOpen} accountName={accountName} hasSession={hasSession} onRegenerated={handleRegenerated} />
      </Panel>
      <Panel title="All files" count={workdriveTree?.total_files || 0}>
        {/* Prompt 172 §5.2: this tree spans the whole account. Without the label, a prior term's bundle reads as the focused term's. */}
        <p className="mb-2 text-[11px] italic text-text-dim">Every file on this account in WorkDrive, across all terms and years. This policy's paperwork is above.</p>
        <DocumentsPanel documents={workdriveTree} bare />
      </Panel>
    </>
  );
}
